import { getUserEnumLabel, getUserTypeLabel } from '../../util/userDisplayHelpers';

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160"%3E%3Crect fill="%23DDE2E6" width="160" height="160"/%3E%3C/svg%3E';

/**
 * @param {Object|null} image
 * @param {string[]} preferredKeys
 * @returns {string|null}
 */
const getImageUrl = (image, preferredKeys = []) => {
  const variants = image?.attributes?.variants || {};
  for (const key of preferredKeys) {
    if (variants[key]?.url) {
      return variants[key].url;
    }
  }
  const first = Object.values(variants).find(v => v?.url);
  return first?.url || null;
};

/**
 * @param {Object} publicData
 * @param {string} key
 * @returns {string|null}
 */
const resolveShortText = (publicData, key) => {
  const value = publicData?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

/**
 * Map a Sharetribe user to Racing Theme member identity DTO.
 *
 * @param {Object|null} user
 * @param {Object} params
 * @param {Object} [params.config]
 * @param {string} [params.fallbackName]
 * @returns {{
 *   name: string,
 *   logoUrl: string,
 *   logoAlt: string,
 *   meta: Array<{icon: string, label: string, value: string}>,
 *   about: string|null,
 *   location: {
 *     city: string|null,
 *     state: string|null,
 *     country: string|null,
 *     headquarters: string|null,
 *     territory: string|null,
 *     family: string|null,
 *   }
 * }}
 */
export const mapUserToMemberIdentity = (user, { config, fallbackName = 'Member' } = {}) => {
  const profile = user?.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const userTypes = config?.user?.userTypes || [];

  const displayName = profile.displayName;
  const name =
    resolveShortText(publicData, 'team_name') ||
    resolveShortText(publicData, 'uf_name') ||
    displayName ||
    fallbackName;

  const logoUrl =
    getImageUrl(user?.profileImage, ['square-small2x', 'square-small', 'default']) ||
    PLACEHOLDER_IMAGE;

  const userTypeId = publicData.userType;
  const memberType = getUserTypeLabel(userTypes, userTypeId);
  const races = getUserEnumLabel(config, 'uf_estimated_races', publicData.uf_estimated_races);
  const territory = getUserEnumLabel(config, 'uf_racing_territory', publicData.uf_racing_territory);
  const family = getUserEnumLabel(config, 'uf_racing_family', publicData.uf_racing_family);
  const affiliation = resolveShortText(publicData, 'uf_affiliation');
  const city = resolveShortText(publicData, 'uf_city');
  const state = getUserEnumLabel(config, 'uf_us_state', publicData.uf_us_state);
  const country = getUserEnumLabel(config, 'uf_country', publicData.uf_country);
  const headquarters =
    [city, state, country].filter(value => value && value !== '-').join(', ') || '-';

  const meta = [];
  if (memberType) {
    meta.push({ icon: 'members', label: 'Member Type', value: memberType });
  }
  if (races) {
    meta.push({ icon: 'races', label: 'Annual Races', value: races });
  }
  if (territory) {
    meta.push({ icon: 'territory', label: 'Racing Territory', value: territory });
  }
  if (family) {
    meta.push({ icon: 'family', label: 'Racing Family', value: family });
  }
  if (affiliation) {
    meta.push({ icon: 'subfamily', label: 'Affiliation', value: affiliation });
  }
  if (headquarters) {
    meta.push({ icon: 'hq', label: 'Headquarters', value: headquarters });
  }
  if (meta.length === 0) {
    meta.push({ icon: 'members', label: 'Member', value: name });
  }

  const about = resolveShortText(publicData, 'uf_about_us_text') || profile.bio || null;

  return {
    name,
    logoUrl,
    logoAlt: name,
    meta,
    about,
    location: {
      city,
      state,
      country,
      headquarters,
      territory,
      family,
    },
  };
};
